// validation-display strategy for the accessible-form-validation flow
// controls was-validated / invalid-feedback / is-invalid-group behavior so consumer signup and QHP controllers
// present errors identically 
// visible error text always lives in the l10n `.invalid-feedback` containers: this helper only toggles their visibility.

// this is just the class assigned to radio groups to make them easier to navigate to
const RADIO_GROUP_SELECTOR = '.accessible-form-radio-group'

// marker passed to setCustomValidity, it flips a field to :invalid to indicate to the browser that the field is invalid
// when setCustomValidity(INVALID_MARK) is called on an input
// the input becomes :invalid and the browser applies native validation styles (e.g. red outline) and allows CSS to target it with :invalid selectors
// which prevents the form from being submitted
// also note -> the actual error message shown to the user comes from the l10n'd .invalid-feedback divs rendered in the view, which are toggled visible when a field is invalid
const INVALID_MARK = 'invalid'

// resolve the invalid-feedback element a field owns (mainly applies to ssn/no_ssn and other cross-field combos)
// A field opts in by carrying data-error-code="<code>", matched to a `.invalid-feedback[data-error-code="<code>"]` anywhere in
// the form. This handles cases where the feedback cannot be a direct sibling (e.g. a checkbox nested
// inside its <label>). Falls back to the radio group's sibling, then the field's next sibling.
function feedbackFor(field) {
  const code = field.dataset.errorCode
  if (code) {
    const root = field.closest('form') || field.getRootNode()
    const byCode = root.querySelector(`.invalid-feedback[data-error-code="${code}"]`)
    if (byCode) return byCode
  }
  const group = field.closest(RADIO_GROUP_SELECTOR)

  // if not a cross-field case (no feedback found by code) OR is not a radio button group,
  // assume the feedback element is the next sibling of the group or the field itself
  return group?.nextElementSibling || field.nextElementSibling
}

// helper method solely for handling radio button groups
// which need to be marked invalid as a group, 
// but may have their feedback element in various locations depending on the markup
function setGroupValidity(field, message) {
  if (field.type === 'radio' && field.name) {
    // default scope to form, but fall back to the document in case
    const scope = field.form || document
    // all radio buttons in the group need to be targeted with setCustomValidity so the group is marked invalid and the inverse for clearing the error
    scope.querySelectorAll(`input[type="radio"][name="${field.name}"]`).forEach(r => r.setCustomValidity(message))
  } else {
    field.setCustomValidity(message)
  }
}

// display the error state for one resolved element: mainly special handling for radio groups
// outline its radio group (and drop the `px-0` reset so the group's intended padding shows) and reveal its matching invalid-feedback. Shared by
// applyResult (cross-field rules) and finalizeForm (native-invalid fields) so both behave identically.
// function is idempotent — re-applying to an already-surfaced field is a no-op.
export function surfaceFieldError(field) {
  const group = field.closest(RADIO_GROUP_SELECTOR)
  if (group) {
    group.classList.add('is-invalid-group')
    group.classList.remove('px-0')
  }
  const feedback = feedbackFor(field)
  if (feedback?.classList.contains('invalid-feedback')) feedback.classList.add('d-block')
}

// apply a single validator result { valid, target, code } to a resolved field element.
// valid = true clears any existing error state; valid = false applies the error state and reveals the feedback element matching the result's code
// target = used to resolve the field element within the controller and apply the error state (setCustomValidity) which flips the :invalid selector and prevents form submission
// code = used to match the feedback element corresponding to the validation error
export function applyResult(field, result) {
  if (!field) return
  // used when user is actively updating a field
  if (result.valid) { removeFieldErrorState(field); return }

  // marks :invalid; visible text comes from the l10n feedback div
  setGroupValidity(field, INVALID_MARK)

  // outline the radio group (if any) and reveal the matching invalid-feedback
  surfaceFieldError(field)
}

// clear any error state previously applied to a field
// mainly needed for radio groups + "other" text input combos
export function removeFieldErrorState(field) {
  // ignore if no field or feedback found
  if (!field) return

  // removes error message below field and resets validity
  setGroupValidity(field, '')

  // for radios, also remove the group-level error class (the outline around the radio buttons)
  // also readds classes removed by is-invalid-group to restore original spacing
  const group = field.closest(RADIO_GROUP_SELECTOR)
  if (group) {
    group.classList.remove('is-invalid-group')
    group.classList.add('px-0')
  }
  const feedback = feedbackFor(field)
  if (feedback?.classList.contains('invalid-feedback')) feedback.classList.remove('d-block')
}

function isHidden(el) {
  if (el.closest('.hide, .hidden')) return true
  const style = window.getComputedStyle(el)
  return style.display === 'none' || style.visibility === 'hidden'
}

// run vals on a form before submit: native constraint-validation styling, surface radio-group +
// invalid-feedback for any remaining :invalid fields, then scroll/focus the first invalid field.
// returns true when the form has no *visible* invalid fields. checkValidity() alone can't be trusted
// here because it also fails on hidden invalid fields, which the user can neither see nor correct.
export function finalizeForm(form) {
  form.classList.add('was-validated')

  const invalidFields = Array.from(form.querySelectorAll(':invalid')).filter(el => !isHidden(el))
  if (invalidFields.length === 0) return true

  invalidFields.forEach(el => surfaceFieldError(el))

  // find first invalid and scroll into view + focus for accessibility
  const firstInvalid = invalidFields[0]
  if (firstInvalid) {
    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' })
    
    // if the invalid element is a fieldset, focus the first focusable control
    // within it — the .accessible-form-radio-group container is a <div> with no
    // tabindex, so calling .focus() on it does nothing
    let focusTarget = firstInvalid
    if (firstInvalid.tagName === 'FIELDSET') {
      focusTarget = firstInvalid.querySelector('input, select, textarea')
    }

    focusTarget?.focus()
  }
  return false
}
